<template>
  <div class="container mx-auto px-4 py-6 space-y-6">
    <!-- Project List Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mt-8">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3 rounded-t-lg">
        <div class="flex items-center justify-between">
          <h1 class="font-semibold text-white text-xl">
            {{ $t('Mobile.MobileProjectList.title') }}
          </h1>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-white">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>
          </div>
        </div>
      </div>

      <div class="p-4">
        <!-- Tab Navigation -->
        <div class="flex mb-6 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            class="flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200"
            :class="[
              !showArchived
                ? 'bg-white text-ccm-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            ]"
            @click="showArchived = false"
          >
            <div class="flex items-center justify-center space-x-2">
              <span>{{ $t('Mobile.MobileProjectList.liveTab') }}</span>
            </div>
          </button>

          <button
            type="button"
            class="flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200"
            :class="[
              showArchived
                ? 'bg-white text-ccm-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            ]"
            @click="showArchived = true"
          >
            <div class="flex items-center justify-center space-x-2">
              <span>{{ $t('Mobile.MobileProjectList.archivedTab') }}</span>
            </div>
          </button>
        </div>

        <!-- Project List -->
        <Transition
          enter-active-class="animate__animated animate__fadeIn animate__fast"
          mode="out-in"
        >
          <div
            v-if="showArchived"
            class="space-y-3"
            data-test="archived-project-list"
          >
            <div
              v-if="archivedProjects.length === 0"
              class="text-center py-8"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12 text-gray-300 mx-auto mb-3">
    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>

              <p class="text-gray-500 text-sm">
                {{ $t('Mobile.MobileProjectList.noArchivedProjects') }}
              </p>
            </div>

            <RouterLink
              v-for="project in archivedProjects"
              :key="project.id"
              :to="{ name: 'MobileProjectPage', params: { projectId: project.id } }"
              class="block bg-gray-50 hover:bg-gray-100 rounded-lg p-4 transition-all duration-200 border border-gray-200 hover:border-gray-300"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div>
                    <p class="text-xs text-gray-500">
                      {{ project.reference }}
                    </p>

                    <h3 class="font-medium text-gray-900 text-sm">
                      {{ project.name }}
                    </h3>
                  </div>
                </div>

                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 text-gray-400">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
              </div>
            </RouterLink>
          </div>

          <div
            v-else
            class="space-y-3"
            data-test="project-list"
          >
            <div
              v-if="projects.length === 0"
              class="text-center py-8"
            >
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-12 h-12 text-gray-300 mx-auto mb-3">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>

              <p class="text-gray-500 text-sm">
                {{ $t('Mobile.MobileProjectList.noLiveProjects') }}
              </p>
            </div>

            <RouterLink
              v-for="project in projects"
              :key="project.id"
              :to="{ name: 'MobileProjectPage', params: { projectId: project.id } }"
              class="block bg-blue-50/35 rounded-lg p-4 transition-all duration-200 border border-ccm-blue-200"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div>
                    <p class="text-xs text-gray-500">
                      {{ project.reference }}
                    </p>

                    <h3 class="font-medium text-gray-900 text-sm">
                      {{ project.name }}
                    </h3>
                  </div>
                </div>

                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="text-ccm-blue-200 w-4 h-4">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
              </div>
            </RouterLink>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useProjectsStore } from '@/store/projects';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const projectsStore = useProjectsStore();
const router = useRouter();

const projects = computed(() => {
  return projectsStore.models.whereNull('archived_at').get();
});

watch(projects, (newValue) => {
  if(newValue.length === 1) {
    router.isReady().then(() => {
      setTimeout(() => {
        router.push({ name: 'MobileProjectPage', params: { projectId: newValue[0].id } });
      }, 1);
    });
  }
}, { immediate: true });

const showArchived = ref(false);

const archivedProjects = computed(() => {
  return projectsStore.models.whereNotNull('archived_at').get();
});
</script>

<style scoped></style>
